import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit, Trash2, X, Clock, CheckCircle, Eye } from 'lucide-react';
import api from '../../utils/api';

const SUBJECT_COLORS = {
  Mathematics: '#3b82f6',
  Science: '#10b981',
  English: '#b8860b',
  Hindi: '#f472b6',
  'Social Studies': '#14b8a6',
  'Computer Science': '#d4a053',
  Kannada: '#ef4444',
  'Physical Education': '#eab308'
};

const CLASS_OPTIONS = Array.from({ length: 6 }, (_, i) => i + 5);
const SECTION_OPTIONS = ['A', 'B'];
const SUBJECT_OPTIONS = [
  'Mathematics',
  'Science',
  'English',
  'Hindi',
  'Social Studies',
  'Computer Science',
  'Kannada',
  'Physical Education'
];

export default function HomeworkPage() {
  // Filter state
  const [selectedClass, setSelectedClass] = useState(5);
  const [selectedSection, setSelectedSection] = useState('All');
  const [selectedSubject, setSelectedSubject] = useState('');

  // Homework list state
  const [homework, setHomework] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    class: 5,
    section: 'A',
    subject: '',
    title: '',
    description: '',
    assignedDate: new Date().toISOString().split('T')[0],
    dueDate: new Date().toISOString().split('T')[0],
    maxMarks: ''
  });
  const [isEditing, setIsEditing] = useState(false);

  // Load homework data
  const loadHomework = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const params = new URLSearchParams();
      if (selectedClass) params.append('class', selectedClass);
      if (selectedSection !== 'All') params.append('section', selectedSection);
      if (selectedSubject) params.append('subject', selectedSubject);

      const response = await api.get(`/homework?${params.toString()}`);
      const homeworkData = response.data.homework || response.data || [];
      setHomework(homeworkData);
      setMessage({ type: 'success', text: `Loaded ${homeworkData.length} homework records` });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to load homework'
      });
      setHomework([]);
    } finally {
      setLoading(false);
    }
  };

  // Load homework on component mount and when filters change
  useEffect(() => {
    loadHomework();
  }, [selectedClass, selectedSection, selectedSubject]);

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Open add homework modal
  const openAddModal = () => {
    setFormData({
      class: selectedClass,
      section: 'A',
      subject: '',
      title: '',
      description: '',
      assignedDate: new Date().toISOString().split('T')[0],
      dueDate: new Date().toISOString().split('T')[0],
      maxMarks: ''
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  // Open edit modal
  const openEditModal = (hw) => {
    setFormData({
      class: hw.class,
      section: hw.section || 'A',
      subject: hw.subject,
      title: hw.title,
      description: hw.description,
      assignedDate: hw.assignedDate.split('T')[0],
      dueDate: hw.dueDate.split('T')[0],
      maxMarks: hw.maxMarks
    });
    setSelectedHomework(hw);
    setIsEditing(true);
    setShowAddModal(true);
  };

  // Close add/edit modal
  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEditing(false);
    setSelectedHomework(null);
  };

  // Submit add/edit form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    // Validation
    if (!formData.subject || !formData.title || !formData.dueDate) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        class: parseInt(formData.class),
        section: formData.section,
        subject: formData.subject,
        title: formData.title,
        description: formData.description,
        assignedDate: formData.assignedDate,
        dueDate: formData.dueDate,
        maxMarks: formData.maxMarks ? parseInt(formData.maxMarks) : null
      };

      if (isEditing) {
        await api.put(`/homework/${selectedHomework.id}`, payload);
        setMessage({ type: 'success', text: 'Homework updated successfully' });
      } else {
        await api.post('/homework', payload);
        setMessage({ type: 'success', text: 'Homework added successfully' });
      }

      closeAddModal();
      await loadHomework();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to save homework'
      });
    } finally {
      setLoading(false);
    }
  };

  // Open view modal
  const openViewModal = (hw) => {
    setSelectedHomework(hw);
    setShowViewModal(true);
  };

  // Close view modal
  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedHomework(null);
  };

  // Open delete confirmation
  const openDeleteConfirm = (hw) => {
    setSelectedHomework(hw);
    setShowDeleteConfirm(true);
  };

  // Confirm delete
  const handleDeleteConfirm = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await api.delete(`/homework/${selectedHomework.id}`);
      setMessage({ type: 'success', text: 'Homework deleted successfully' });
      setShowDeleteConfirm(false);
      setSelectedHomework(null);
      await loadHomework();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to delete homework'
      });
    } finally {
      setLoading(false);
    }
  };

  // Close delete confirmation
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setSelectedHomework(null);
  };

  // Filter homework by tab
  const getFilteredHomework = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return homework.filter(hw => {
      const dueDate = new Date(hw.dueDate);
      dueDate.setHours(0, 0, 0, 0);

      if (activeTab === 'active') {
        return dueDate >= today;
      } else {
        return dueDate < today;
      }
    });
  };

  // Get days until/overdue
  const getDaysInfo = (dueDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diff = Math.floor((due - today) / (1000 * 60 * 60 * 24));

    if (diff > 0) {
      return { text: `Due in ${diff} day${diff > 1 ? 's' : ''}`, color: '#10b981', icon: Clock };
    } else if (diff === 0) {
      return { text: 'Due today', color: '#f472b6', icon: Clock };
    } else {
      return { text: `Overdue by ${Math.abs(diff)} day${Math.abs(diff) > 1 ? 's' : ''}`, color: '#ef4444', icon: Clock };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Truncate description
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  const filteredHomework = getFilteredHomework();
  const subjectColor = selectedHomework ? SUBJECT_COLORS[selectedHomework.subject] : '#3b82f6';
  const daysInfo = selectedHomework ? getDaysInfo(selectedHomework.dueDate) : null;

  return (
    <div className="homework-page">
      <div className="card">
        <div className="card-header">
          <h1>
            <BookOpen size={28} />
            Homework Management
          </h1>
        </div>

        <div className="card-body">
          {/* Message Display */}
          {message.text && (
            <div className={`message message-${message.type}`} style={{ marginBottom: '20px' }}>
              {message.text}
            </div>
          )}

          {/* Filter Bar */}
          <div className="filter-bar form-row" style={{ marginBottom: '20px' }}>
            <div className="filter-group">
              <label htmlFor="hw-class">Class</label>
              <select
                id="hw-class"
                className="form-control"
                value={selectedClass}
                onChange={(e) => setSelectedClass(Number(e.target.value))}
                disabled={loading}
              >
                {CLASS_OPTIONS.map(classNum => (
                  <option key={classNum} value={classNum}>Class {classNum}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="hw-section">Section</label>
              <select
                id="hw-section"
                className="form-control"
                value={selectedSection}
                onChange={(e) => setSelectedSection(e.target.value)}
                disabled={loading}
              >
                <option value="All">All</option>
                {SECTION_OPTIONS.map(sec => (
                  <option key={sec} value={sec}>{sec}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="hw-subject">Subject</label>
              <select
                id="hw-subject"
                className="form-control"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                disabled={loading}
              >
                <option value="">All Subjects</option>
                {SUBJECT_OPTIONS.map(subj => (
                  <option key={subj} value={subj}>{subj}</option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-primary"
              onClick={openAddModal}
              disabled={loading}
              style={{ alignSelf: 'flex-end' }}
            >
              <Plus size={18} />
              Add Homework
            </button>
          </div>

          {/* Tabs */}
          <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
            <button
              className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === 'active' ? '2px solid #3b82f6' : 'none',
                color: activeTab === 'active' ? '#3b82f6' : '#6b7280',
                fontWeight: activeTab === 'active' ? '600' : '500',
                fontSize: '14px'
              }}
            >
              Active Homework
            </button>
            <button
              className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
              onClick={() => setActiveTab('past')}
              style={{
                padding: '10px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                borderBottom: activeTab === 'past' ? '2px solid #3b82f6' : 'none',
                color: activeTab === 'past' ? '#3b82f6' : '#6b7280',
                fontWeight: activeTab === 'past' ? '600' : '500',
                fontSize: '14px'
              }}
            >
              Past Homework
            </button>
          </div>

          {/* Homework Cards */}
          {filteredHomework.length > 0 ? (
            <div className="homework-cards">
              {filteredHomework.map(hw => {
                const daysData = getDaysInfo(hw.dueDate);
                const badgeColor = SUBJECT_COLORS[hw.subject] || '#3b82f6';

                return (
                  <div
                    key={hw.id}
                    style={{
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                      padding: '16px',
                      border: '1px solid #e5e7eb',
                      marginBottom: '12px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                      transition: 'box-shadow 0.2s'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', flex: 1 }}>
                        <div
                          className="badge"
                          style={{
                            backgroundColor: badgeColor,
                            color: '#ffffff',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {hw.subject}
                        </div>
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>
                            {hw.title}
                          </h3>
                          <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6b7280' }}>
                            Class {hw.class} {hw.section ? `- Section ${hw.section}` : ''}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => openViewModal(hw)}
                          style={{
                            padding: '6px 8px',
                            background: 'none',
                            border: '1px solid #d1d5db',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="View"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => openEditModal(hw)}
                          style={{
                            padding: '6px 8px',
                            background: 'none',
                            border: '1px solid #d1d5db',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => openDeleteConfirm(hw)}
                          style={{
                            padding: '6px 8px',
                            background: 'none',
                            border: '1px solid #d1d5db',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#ef4444'
                          }}
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px', fontSize: '13px', color: '#6b7280' }}>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Assigned:</span> {formatDate(hw.assignedDate)}
                      </div>
                      <div>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Due:</span> {formatDate(hw.dueDate)}
                      </div>
                    </div>

                    {hw.maxMarks && (
                      <div style={{ marginBottom: '12px', fontSize: '13px' }}>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Max Marks:</span> <span style={{ color: '#6b7280' }}>{hw.maxMarks}</span>
                      </div>
                    )}

                    <div style={{
                      padding: '12px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '6px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#4b5563',
                      lineHeight: '1.5'
                    }}>
                      {truncateText(hw.description)}
                      {hw.description && hw.description.length > 150 && (
                        <button
                          onClick={() => openViewModal(hw)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            padding: '0',
                            marginLeft: '4px'
                          }}
                        >
                          Read more
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        <span style={{ fontWeight: '600', color: '#374151' }}>Assigned by:</span> {hw.assignedByTeacher || 'N/A'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: daysData.color, fontWeight: '600' }}>
                        <daysData.icon size={16} />
                        {daysData.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state" style={{ textAlign: 'center', padding: '40px 20px' }}>
              <BookOpen size={48} style={{ marginBottom: '16px', color: '#d1d5db' }} />
              <p style={{ fontSize: '16px', color: '#6b7280', margin: '0' }}>
                {activeTab === 'active' ? 'No active homework found.' : 'No past homework found.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Homework Modal */}
      {showAddModal && (
        <div
          className="modal-overlay"
          onClick={closeAddModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: '0', fontSize: '18px', fontWeight: '700' }}>
                {isEditing ? 'Edit Homework' : 'Add Homework'}
              </h2>
              <button
                className="close-btn"
                onClick={closeAddModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} style={{ padding: '20px' }}>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label htmlFor="form-class" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Class *
                  </label>
                  <select
                    id="form-class"
                    name="class"
                    className="form-control"
                    value={formData.class}
                    onChange={handleFormChange}
                    required
                  >
                    {CLASS_OPTIONS.map(classNum => (
                      <option key={classNum} value={classNum}>Class {classNum}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="form-section" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Section
                  </label>
                  <select
                    id="form-section"
                    name="section"
                    className="form-control"
                    value={formData.section}
                    onChange={handleFormChange}
                  >
                    {SECTION_OPTIONS.map(sec => (
                      <option key={sec} value={sec}>{sec}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="form-subject" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  Subject *
                </label>
                <select
                  id="form-subject"
                  name="subject"
                  className="form-control"
                  value={formData.subject}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">Select Subject</option>
                  {SUBJECT_OPTIONS.map(subj => (
                    <option key={subj} value={subj}>{subj}</option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="form-title" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  Title *
                </label>
                <input
                  id="form-title"
                  type="text"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleFormChange}
                  placeholder="Homework title"
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="form-description" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  Description
                </label>
                <textarea
                  id="form-description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleFormChange}
                  placeholder="Homework details and instructions"
                  rows="4"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label htmlFor="form-assigned" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Assigned Date
                  </label>
                  <input
                    id="form-assigned"
                    type="date"
                    name="assignedDate"
                    className="form-control"
                    value={formData.assignedDate}
                    onChange={handleFormChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="form-due" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                    Due Date *
                  </label>
                  <input
                    id="form-due"
                    type="date"
                    name="dueDate"
                    className="form-control"
                    value={formData.dueDate}
                    onChange={handleFormChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="form-maxmarks" style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>
                  Max Marks
                </label>
                <input
                  id="form-maxmarks"
                  type="number"
                  name="maxMarks"
                  className="form-control"
                  value={formData.maxMarks}
                  onChange={handleFormChange}
                  placeholder="e.g., 50"
                  min="0"
                />
              </div>

              <div className="modal-footer" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAddModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {isEditing ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Homework Modal */}
      {showViewModal && selectedHomework && (
        <div
          className="modal-overlay"
          onClick={closeViewModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
          >
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div
                  className="badge"
                  style={{
                    backgroundColor: SUBJECT_COLORS[selectedHomework.subject] || '#3b82f6',
                    color: '#ffffff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'inline-block'
                  }}
                >
                  {selectedHomework.subject}
                </div>
                <h2 style={{ margin: '8px 0 0 0', fontSize: '20px', fontWeight: '700' }}>
                  {selectedHomework.title}
                </h2>
              </div>
              <button
                className="close-btn"
                onClick={closeViewModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>CLASS & SECTION</h3>
                <p style={{ fontSize: '16px', color: '#1f2937', margin: '0' }}>Class {selectedHomework.class} {selectedHomework.section ? `- Section ${selectedHomework.section}` : ''}</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>ASSIGNED DATE</h3>
                  <p style={{ fontSize: '16px', color: '#1f2937', margin: '0' }}>{formatDate(selectedHomework.assignedDate)}</p>
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>DUE DATE</h3>
                  <p style={{ fontSize: '16px', color: '#1f2937', margin: '0' }}>{formatDate(selectedHomework.dueDate)}</p>
                </div>
              </div>

              {selectedHomework.maxMarks && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>MAX MARKS</h3>
                  <p style={{ fontSize: '16px', color: '#1f2937', margin: '0' }}>{selectedHomework.maxMarks}</p>
                </div>
              )}

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>ASSIGNED BY</h3>
                <p style={{ fontSize: '16px', color: '#1f2937', margin: '0' }}>{selectedHomework.assignedByTeacher || 'N/A'}</p>
              </div>

              <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#6b7280', margin: '0 0 8px 0' }}>DESCRIPTION</h3>
                <p style={{
                  fontSize: '14px',
                  color: '#4b5563',
                  margin: '0',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word'
                }}>
                  {selectedHomework.description || 'No description provided'}
                </p>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: getDaysInfo(selectedHomework.dueDate).color }}>
                <Clock size={18} />
                {getDaysInfo(selectedHomework.dueDate).text}
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={closeViewModal}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  openEditModal(selectedHomework);
                  closeViewModal();
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Edit size={16} />
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedHomework && (
        <div
          className="modal-overlay"
          onClick={closeDeleteConfirm}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              maxWidth: '400px',
              width: '90%'
            }}
          >
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: '0', fontSize: '18px', fontWeight: '700', color: '#1f2937' }}>
                Delete Homework?
              </h2>
            </div>

            <div className="modal-body" style={{ padding: '20px' }}>
              <p style={{ margin: '0', fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                Are you sure you want to delete "{selectedHomework.title}"? This action cannot be undone.
              </p>
            </div>

            <div className="modal-footer" style={{ padding: '20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={closeDeleteConfirm}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
